/**
 * P-1.3 "Hand chats to": the setting as the page holds it (the BFF's DTO, `teamId: null` when
 * there is none, `labels: []` when none) and as the BFF takes it (`teamId` only for a team,
 * `labels` only when there are some: its schema is strict).
 *
 * Task review item 11 added labels to it, and the same person-or-team and labels to a task's
 * Hand off step (`handTo`, `labels`). The label rules are the sidecar's `tasks/hand-to.ts`,
 * restated: at most five, each once, Chatwoot's own title rule (letters and digits of any
 * script, `-` and `_`), lower-case.
 */

export const MAX_HANDOVER_LABELS = 5;
export const MAX_HANDOVER_LABEL_CHARS = 255;
export const HANDOVER_LABEL_PATTERN = /^[\p{L}\p{N}_-]+$/u;

/** One label the sidecar takes. */
export const isHandoverLabel = label =>
  typeof label === 'string' &&
  label.length > 0 &&
  label.length <= MAX_HANDOVER_LABEL_CHARS &&
  HANDOVER_LABEL_PATTERN.test(label) &&
  label === label.toLowerCase();

/** A list of labels the sidecar takes: at most five, each valid, each once. */
export const validHandoverLabels = labels =>
  Array.isArray(labels) &&
  labels.length <= MAX_HANDOVER_LABELS &&
  labels.every(isHandoverLabel) &&
  new Set(labels).size === labels.length;

const labelsOf = setting =>
  Array.isArray(setting?.labels) ? setting.labels : [];

/** The PATCH value, or `null` to clear it. */
export const handoverPatch = setting => {
  if (setting === null || setting === undefined) return null;
  const labels = labelsOf(setting);
  return {
    to: setting.to,
    personId: setting.personId,
    ...(setting.to === 'team' && setting.teamId !== null
      ? { teamId: setting.teamId }
      : {}),
    noteLang: setting.noteLang,
    ...(labels.length ? { labels: [...labels] } : {}),
  };
};

/** Two settings that say the same thing (the dirty check). */
export const sameHandover = (a, b) =>
  JSON.stringify(handoverPatch(a)) === JSON.stringify(handoverPatch(b));

/** The notes the team reads are written in the dashboard's language (spec §1 open question 1). */
export const noteLangOf = locale =>
  String(locale ?? '')
    .toLowerCase()
    .startsWith('ar')
    ? 'ar'
    : 'en';

const positiveInt = value => Number.isInteger(value) && value > 0;

/**
 * A Hand off step's `handTo`, as the step screen's three choices read it: `account` (absent:
 * the account's "Hand chats to"), `person` or `team`, with the id or `null`.
 */
export const handToChoiceOf = step => {
  const handTo = step?.handTo;
  if (handTo?.kind === 'person') {
    return {
      to: 'person',
      id: positiveInt(handTo.personId) ? handTo.personId : null,
    };
  }
  if (handTo?.kind === 'team') {
    return {
      to: 'team',
      id: positiveInt(handTo.teamId) ? handTo.teamId : null,
    };
  }
  return { to: 'account', id: null };
};

/**
 * The step fields a choice writes. `handTo: undefined` takes the key off (absent is the
 * account's setting), and a person or team with no id yet keeps the kind with no id, which
 * the local check reports until one is picked.
 */
export const handToPatch = (to, id) => {
  if (to === 'person') {
    return { handTo: { kind: 'person', personId: id ?? null } };
  }
  if (to === 'team') return { handTo: { kind: 'team', teamId: id ?? null } };
  return { handTo: undefined };
};

/** A step's `handTo` the sidecar takes, or absent. */
export const validHandTo = handTo => {
  if (handTo === undefined) return true;
  if (handTo?.kind === 'person') {
    return positiveInt(handTo.personId) && Object.keys(handTo).length === 2;
  }
  if (handTo?.kind === 'team') {
    return positiveInt(handTo.teamId) && Object.keys(handTo).length === 2;
  }
  return false;
};

/** `labels` toggled: added at the end, or taken off. Never past the cap. */
export const toggleLabel = (labels, title) => {
  const now = Array.isArray(labels) ? labels : [];
  if (now.includes(title)) return now.filter(label => label !== title);
  if (now.length >= MAX_HANDOVER_LABELS) return now;
  return [...now, title];
};
